import React, {
  ComponentProps,
  Dispatch,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'

import { Observable } from '@apollo/client'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'

import { DateNavigation, ResolvableMeta } from 'modules/core/lib/navigation/routes'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'
import { Meta } from 'modules/core/lib/navigation/types'

import { useMetaContext } from './matches'

export type ResolvedMeta = {
  pathname: string
  data: Meta
}

export type RouteMetaWithPathname = {
  pathname: string
  meta: ResolvableMeta<Meta>
  resource: boolean
  filters?: {
    date?: DateNavigation
  }
}

interface ResponsivePageContext {
  routeMeta: RouteMetaWithPathname | null
  setActionHandler: (actionHandler: (key: string) => void) => void
  headerOverride: Partial<ResourceHeadersPropsWithActions> | null
  setHeaderOverride: Dispatch<SetStateAction<Partial<ResourceHeadersPropsWithActions> | null>>
  updateMetadataProperty: <
    Value extends Extract<
      NonNullable<NonNullable<ComponentProps<typeof ResourceHeader>['metadata']>[number]>,
      object
    >['value']
  >(
    label: string,
    value: Value
  ) => void
}

const ResponsivePageContext = React.createContext<ResponsivePageContext | undefined>(undefined)
export const ResponsivePageProvider = ResponsivePageContext.Provider

export const useResponsivePage = () => {
  const context = useContext(ResponsivePageContext)

  if (context === undefined) {
    throw 'useResponsivePage must be used within a ResponsivePageContext'
  }

  return context
}

/**
 * Hook to get and update resource header props dynamically.
 * This allows page components to update the header title, description, actions, metadata,
 * and all other props in real-time when data changes, even if the route meta hasn't updated yet.
 *
 * The `updateHeader` function supports partial updates - you can update just the properties
 * that changed, and they will be merged with the existing route meta header props.
 *
 * @example
 * ```tsx
 * const { headerProps, updateHeader } = useResourceHeader()
 *
 * // Update just the title when it changes
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       title: data.resource.title,
 *     })
 *   }
 * }, [data?.resource?.title, updateHeader])
 *
 * // Update description separately
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       description: data.resource.description,
 *     })
 *   }
 * }, [data?.resource?.description, updateHeader])
 *
 * // Update actions when permissions change
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       primaryAction: canEdit ? { label: 'Edit', key: 'edit' } : undefined,
 *       otherActions: canDelete ? [{ label: 'Delete', key: 'delete', critical: true }] : undefined,
 *     })
 *   }
 * }, [canEdit, canDelete, updateHeader])
 *
 * // Update metadata
 * useEffect(() => {
 *   if (data?.resource) {
 *     updateHeader({
 *       metadata: [
 *         { label: 'Status', value: { type: 'text', content: data.resource.status } },
 *       ],
 *     })
 *   }
 * }, [data?.resource?.status, updateHeader])
 *
 * // Clear override to go back to route meta
 * const resetHeader = () => updateHeader(null)
 * ```
 */
export const useResourceHeader = () => {
  const context = useResponsivePage()

  return {
    headerProps: context.headerOverride,
    updateHeader: context.setHeaderOverride,
    updateMetadataProperty: context.updateMetadataProperty,
  }
}

export function useRouteMeta(routeMeta: RouteMetaWithPathname | null): ResolvedMeta | null {
  const [resolvedMeta, setResolvedMeta] = useState<ResolvedMeta | null>(null)
  const { context } = useMetaContext()

  useLayoutEffect(() => {
    if (!routeMeta) return
    const { meta, pathname } = routeMeta
    const pendingMeta = meta(context)

    if (pendingMeta instanceof Observable) {
      const subscription = pendingMeta.subscribe((metaData) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: metaData,
        })
      })

      return () => subscription.unsubscribe()
    }

    if (pendingMeta instanceof Promise) {
      pendingMeta.then((metaData) => {
        setResolvedMeta({
          pathname: routeMeta.pathname,
          data: metaData,
        })
      })
      return
    }

    setResolvedMeta({
      pathname,
      data: pendingMeta,
    })
  }, [routeMeta, context])

  return resolvedMeta
}
